import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Nav, Navbar, NavDropdown} from "react-bootstrap";
import {Link} from "react-router";
import {
    CANOPIA,
    CANOPIA_ADMIN,
    CANOPIA_OAUTH2,
    CANOPIA_REVIEW,
    CANOPIA_SALES, CANOPIA_SUPER_ADMIN
} from "../canopia/CanopiaUtils";
import {useTranslation} from 'react-i18next';
import AuthService from "../../services/auth.service";
import {useCookies} from "react-cookie";

export default function Navigation() {

    const {t, i18n} = useTranslation();

    const [user, setUser] = useState(null);

    const languages = useMemo(() => [
        {key: 'en', label: 'En'},
        {key: 'fr', label: 'Fr'},
        // {key: 'de', label: 'De'}
    ], []);

    const [cookies, setCookie] = useCookies(['lang']);

    const [language, setLanguage] = useState(languages[0]);

    const findLanguage = useCallback(key => {
        return languages.find(l => l.key === key);
    }, [languages]);

    const changeLanguageHandler = useCallback(lang => {
        setLanguage(lang);
        setCookie('lang', lang.key, {path: '/'})
        i18n.changeLanguage(lang.key);
    }, [setCookie, i18n]);

    useEffect(() => {
        const currentUser = AuthService.getCurrentUser();
        if (currentUser) {
            setUser(currentUser);
        }
    }, [setUser]);

    useEffect(() => {
        // Get the language
        if (cookies && cookies.lang) {
            const lang = findLanguage(cookies.lang);
            if (lang) {
                changeLanguageHandler(lang);
            }
        }
    }, [changeLanguageHandler, findLanguage, cookies]);

    const languageNav = <Nav className="ml-auto">
        <NavDropdown align="end" title={<span className={'canopia-navbar-text'}>{language.label}</span>}
                     id="basic-nav-dropdown">
            {languages.map(l => {
                return <NavDropdown.Item key={l.key} as={Link} onClick={() => changeLanguageHandler(l)}>
                    <span className={'canopia-navdd-link'}>{l.label}</span>
                </NavDropdown.Item>
            })}
        </NavDropdown>
    </Nav>

    if (!user || !user.roles.includes(CANOPIA)) {
        return (
            <Navbar expand={'lg'} className={'bg-navbar'}>
                <Nav className="me-auto">
                    <Link to={"/login"} className={'nav-link'}>
                        <span className={'canopia-navbar-link'}>{t('login')}</span>
                    </Link>
                </Nav>
                {/*{languageNav}*/}
            </Navbar>
        );
    }

    const isCanopiaAdmin = user.roles.includes(CANOPIA_ADMIN);
    const isSuperAdmin = user.roles.includes(CANOPIA_SUPER_ADMIN);
    const isCanopiaReview = user.roles.includes(CANOPIA_REVIEW);
    const isCanopiaSales = user.roles.includes(CANOPIA_SALES);
    const isCanopiaOAuth2 = user.roles.includes(CANOPIA_OAUTH2); // OAuth2 users cannot change their password

    return (
        <Navbar expand={'lg'} className={'bg-navbar'}>
            <Link to={"/portfolio"} className={'navbar-brand'}>
                <span className={'canopia-navbar-brand'}>{t('portfolio')}</span>
            </Link>
            <Navbar.Toggle aria-controls="basic-navbar-nav"/>
            <Navbar.Collapse id="basic-navbar-nav">
                <Nav className="me-auto">
                    <Link to={"/docs"} className={'nav-link'}>
                        <span className={'canopia-navbar-link'}>{t('docs')}</span>
                    </Link>
                    <Link to={"/policies"} className={'nav-link'}>
                        <span className={'canopia-navbar-link'}>{t('terms')}</span>
                    </Link>
                    {isCanopiaSales && (
                        <NavDropdown title={
                            <span className={'canopia-navbar-link'}>{t('settings')}</span>
                        } id="basic-nav-dropdown">
                            <NavDropdown.Item as={Link} to={"/user-client-settings"}>
                                <span className={'canopia-navdd-link'}>Clients</span>
                            </NavDropdown.Item>
                        </NavDropdown>
                    )}
                    {isCanopiaReview && (
                        <NavDropdown title={
                            <span className={'canopia-navbar-link'}>{t('nav-review')}</span>
                        } id="basic-nav-dropdown">
                            <NavDropdown.Item as={Link} to={"/review-status"}>
                                <span className={'canopia-navdd-link'}>Review Status</span>
                            </NavDropdown.Item>
                        </NavDropdown>
                    )}
                    {isCanopiaAdmin && (
                        <NavDropdown title={
                            <span className={'canopia-navbar-link'}>Admin</span>
                        } id="basic-nav-dropdown">
                            <NavDropdown.Item as={Link} to={"/import"}>
                                <span className={'canopia-navdd-link'}>Import</span>
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to={"/setup"}>
                                <span className={'canopia-navdd-link'}>Setup</span>
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to={"/run"}>
                                <span className={'canopia-navdd-link'}>Run</span>
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to={"/reload"}>
                                <span className={'canopia-navdd-link'}>Reload</span>
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to={"/register"}>
                                <span className={'canopia-navdd-link'}>Register</span>
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to={"/user-clients"}>
                                <span className={'canopia-navdd-link'}>User Clients</span>
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to={"/benchmarks"}>
                                <span className={'canopia-navdd-link'}>{t('benchmark')}</span>
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to={"/fund-subst"}>
                                <span className={'canopia-navdd-link'}>{t('fund_subst')}</span>
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to={"/fund-scans"}>
                                <span className={'canopia-navdd-link'}>{t('fund_scans')}</span>
                            </NavDropdown.Item>
                            {isSuperAdmin && <NavDropdown.Item as={Link} to={"/cron-configs"}>
                                <span className={'canopia-navdd-link'}>Cron Configs</span>
                            </NavDropdown.Item>}
                        </NavDropdown>
                    )}
                </Nav>
                <Nav className="ml-auto">
                    <NavDropdown align="end"
                                 title={<span
                                     className={'canopia-navbar-text'}>{user.firstname + ' ' + user.lastname}</span>}
                                 id="basic-nav-dropdown">
                        {!isCanopiaOAuth2 &&
                            // <NavDropdown.Item as={Link} to={"/change-pwd"}>
                            //     <span className={'canopia-navdd-link'}>{t('chgPwd')}</span>
                            // </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to={"/security"}>
                                <span className={'canopia-navdd-link'}>{t('security')}</span>
                            </NavDropdown.Item>
                        }
                        <NavDropdown.Item as={Link} to={"/logged-out"}>
                            <span className={'canopia-navdd-link'}>{t('logout')}</span>
                        </NavDropdown.Item>
                    </NavDropdown>
                </Nav>
            </Navbar.Collapse>
            {/*{languageNav}*/}
        </Navbar>
    );
}

